import React from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import ProductForm from 'components/ProductLayout/ProductForm';
import type { UploadImageParams, SendProduct, Product } from 'types/Products';
import { handleSuccess, handleError } from 'utils/index';
import {
  REFERENCE_EXISTS_LINE_ONE_MESSAGE,
  REFERENCE_EXISTS_LINE_TWO_MESSAGE,
  UPDATE_MESSAGE
} from 'const/Message';
import Loader from 'components/Loader';
import {
  getProductByCode,
  updateProduct,
  uploadImageV2
} from 'services/productService';
import {
  createPromotion,
  CreatePromotionParams,
  updatePromotion
} from 'services/promotionService';
import { CHANNELS_PROMOTION } from 'const/Const';
import { handleMessageWhenAppliedPromotion } from 'utils/handleResponse';

interface MatchParams {
  code: string;
}

const REFERENCE_EXISTS_MESSAGE = (
  <>
    <div>{REFERENCE_EXISTS_LINE_ONE_MESSAGE}</div>
    <div>{REFERENCE_EXISTS_LINE_TWO_MESSAGE}</div>
  </>
);

const ProductsEdit = () => {
  const { code } = useParams<MatchParams>();
  const queryClient = useQueryClient();
  const history = useHistory();

  const {
    data: result,
    isLoading: isLoadingProductByCode,
    isFetching
  } = useQuery(['getProduct', { code }], ({ signal }) =>
    getProductByCode({ signal, code })
  );
  const { isLoading: isUpdatingProduct, mutate: onUpdateProduct } = useMutation(
    ({ code, product }: { code: string; product: SendProduct }) =>
      updateProduct(code, product)
  );
  const { isLoading: isUploadingImage, mutateAsync: mutateUploadImage } =
    useMutation(({ code, formData }: UploadImageParams) =>
      uploadImageV2(code, formData)
    );
  const { isLoading: isUpdatingPromotion, mutateAsync: mutateUpdatePromotion } =
    useMutation(
      ({
        code,
        promotionData
      }: {
        code: string;
        promotionData: CreatePromotionParams;
      }) => updatePromotion(code, promotionData)
    );
  const { isLoading: isCreatingPromotion, mutateAsync: mutateCreatePromotion } =
    useMutation((data: CreatePromotionParams) => createPromotion(data));

  const onUpdate = (product: Product, imgCropped: any) => {
    const data: SendProduct = {
      name: product.name,
      isOption: false,
      maxAmount: product.maxAmount,
      description: product.description,
      categories: product.categories,
      includedTax: product.includedTax,
      isAlcoholicProduct: product.isAlcoholicProduct,
      dietaryAttributes: product.dietaryAttributes,
      groupOptionIRIs: product.groupOptionIRIs,
      sellingAsOption: product.sellingAsOption,
      allergenAttributes: product.allergenAttributes,
      notMultipleQuantityOption: product.notMultipleQuantityOption,
      isSellWithUnits: product.isSellProductWithUnits,
      ...(product.isSellProductWithUnits
        ? {
            pricePerUnit: Math.round(product.pricePerUnit!),
            quantityProductSoldByWeight: product.quantityProductSoldByWeight,
            unit: product.unit
          }
        : { price: Math.round(product.price!) })
    };

    if (product.reference) {
      data.reference = product.reference;
    }

    const fd = new FormData();

    if (imgCropped) {
      fd.append('files[]', imgCropped);
    }
    if (!imgCropped && product.files) {
      for (let i = 0; i < product.files.length; i++) {
        fd.append('files[]', product.files[i]);
      }
    }

    onUpdateProduct(
      { code: code, product: data },
      {
        onSuccess: async (res: any) => {
          const newCode = res.data.code;

          queryClient.invalidateQueries(['getProduct', { newCode }]);
          queryClient.invalidateQueries('getListGroupOptions');
          queryClient.invalidateQueries('getListProducts');
          queryClient.invalidateQueries('getListCategories');

          handleSuccess(UPDATE_MESSAGE.PRODUCT_SUCCESS);

          let promotionData;

          if (product.startDate && product.endDate && product.isReduction) {
            promotionData = {
              channels: [CHANNELS_PROMOTION],
              name: `Discount percent ${product.reductionPercent}`,
              startDate: product.startDate,
              endDate: product.endDate,
              priority: 1,
              exclusive: true,
              enabled: true,
              addToAllCatalogs: true,
              scopes: [
                {
                  type: 'for_variants',
                  configuration: {
                    variants: [res.data.variants[0].code as string]
                  }
                }
              ],
              actions: [
                {
                  type: 'percentage_discount',
                  configuration: {
                    amount: product.reductionPercent || 0
                  }
                }
              ],
              code: `discount-percent-${Math.floor(
                product.reductionPercent || 0
              )}-${uuidv4()}`
            };
          } else {
            promotionData = {
              enabled: false
            };
          }

          const listApi = [];

          if (product.files === null) {
            listApi.push(mutateUploadImage({ code: newCode, formData: null }));
          }

          if (fd.getAll('files[]').length > 0) {
            listApi.push(mutateUploadImage({ code: newCode, formData: fd }));
          }

          if (
            (product.isReduction || product.appliedPromotion) &&
            promotionData
          ) {
            if (product.promotions) {
              listApi.push(
                mutateUpdatePromotion({
                  code: product.promotions.code,
                  promotionData
                })
              );
            } else {
              listApi.push(mutateCreatePromotion(promotionData));
            }
          }

          await Promise.all(listApi);

          queryClient.invalidateQueries('getProducts');

          if (product.isReduction) {
            handleMessageWhenAppliedPromotion();
          }

          history.push('/menu/products');
        },
        onError: (error: any) => {
          handleError(
            error.response.status,
            error.response.status === 422
              ? REFERENCE_EXISTS_MESSAGE
              : UPDATE_MESSAGE.PRODUCT_ERROR
          );
        }
      }
    );
  };

  return (
    <>
      {(isUpdatingProduct ||
        isLoadingProductByCode ||
        isUploadingImage ||
        isCreatingPromotion ||
        isUpdatingPromotion ||
        isFetching) && <Loader />}
      {result?.data && (
        <ProductForm
          mode="edit"
          dataEdit={result.data}
          edit={onUpdate}></ProductForm>
      )}
    </>
  );
};

export default ProductsEdit;
